import React, { useState } from 'react';
import { Search, Link as LinkIcon, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentLookup = () => {
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = async () => {
    if (!whatsappNumber.trim()) {
      return;
    }

    setIsSearching(true);

    try {
      const response = await axios.post(`${API}/search-payment`, {
        whatsapp_number: whatsappNumber.trim()
      });

      const result = {
        number: whatsappNumber.trim(),
        found: response.data.found,
        customerName: response.data.customer_name || null,
        paymentLink: response.data.payment_link || null
      };

      setSearchResult(result);
    } catch (error) {
      console.error('Error searching for payment:', error);
      const result = {
        number: whatsappNumber.trim(),
        found: false,
        customerName: null,
        paymentLink: null,
        error: 'Terjadi kesalahan saat mencari data'
      };
      setSearchResult(result);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="min-h-screen relative bg-cover bg-center bg-no-repeat" style={{backgroundImage: 'url(https://customer-assets.emergentagent.com/job_search-pay-links/artifacts/wj3ekpgh_1.png)'}}>
      {/* Overlay sangat tipis untuk sedikit meningkatkan keterbacaan */}
      <div className="absolute inset-0 bg-white/20"></div>
      
      <div className="relative z-10 container mx-auto px-3 sm:px-6 py-6 sm:py-12 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-4 sm:mb-6">
          <div className="inline-flex items-center justify-center mb-4 sm:mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_c831c003-18ee-420a-baf1-e328c92b39d3/artifacts/p9rbj63o_skus7etr_1ddb204b-31ba-423a-8a84-fd5570f6a9f4_removalai_preview.png" 
              alt="Mitra Data Nusantara Logo" 
              className="w-72 sm:w-80 md:w-96 h-auto object-contain"
            />
          </div>
          <p className="text-gray-600 text-sm sm:text-base md:text-lg px-2">
            Masukkan nomor WhatsApp Terdaftar untuk melihat link tagihan pembayaran
          </p>
        </div>

        {/* Search Card */}
        <Card className="mb-6 sm:mb-8 shadow-2xl border-2 border-white/50 bg-white/95 backdrop-blur-sm">
          <CardContent className="pt-4 sm:pt-6 px-4 sm:px-6">
            <div className="flex flex-col gap-3 sm:gap-4">
              <div className="relative w-full">
                <svg 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 sm:w-6 sm:h-6" 
                  viewBox="0 0 24 24" 
                  fill="none"
                >
                  <path 
                    d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" 
                    fill="#25D366"
                  />
                </svg>
                <Input
                  type="tel"
                  inputMode="numeric"
                  placeholder="62"
                  value={whatsappNumber}
                  onChange={(e) => setWhatsappNumber(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="pl-11 sm:pl-12 h-14 sm:h-12 text-base sm:text-lg border-green-500 focus:border-green-600 focus:ring-green-500 w-full"
                />
              </div>
              <div className="text-xs sm:text-xs text-gray-700 leading-relaxed px-1 space-y-1">
                <p className="font-bold text-blue-700">Perhatian :</p>
                <div className="flex gap-2">
                  <span className="flex-shrink-0">*</span>
                  <p className="flex-1">Tagihan Pembayaran terbit pada tanggal 1 setiap bulannya, pembayaran paling lama tanggal 5 setiap bulannya untuk menghindari Isolir.</p>
                </div>
                <div className="flex gap-2">
                  <span className="flex-shrink-0">*</span>
                  <p className="flex-1">Jika Pembayaran Telah Selesai Hidupkan Ulang Modem, untuk kembali Online.</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleSearch}
                  disabled={isSearching || !whatsappNumber.trim()}
                  className="min-h-[48px] h-12 sm:h-11 flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-md transition-all duration-200 hover:shadow-lg active:scale-95 text-base sm:text-sm"
                >
                  {isSearching ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 sm:h-4 sm:w-4 border-b-2 border-white mr-2" />
                      Mencari...
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5 sm:w-4 sm:h-4 mr-2" />
                      Cari
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => window.open('https://youtu.be/XqHx4v78z3M?si=Mxw-90WpHs-sXgP3', '_blank')}
                  className="min-h-[48px] h-12 sm:h-11 sm:px-6 bg-red-600 hover:bg-red-700 text-white font-medium shadow-md transition-all duration-200 hover:shadow-lg active:scale-95 text-base sm:text-sm"
                >
                  <svg className="w-5 h-5 sm:w-4 sm:h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  Cara Bayar
                </Button>
              </div>
            </div>

            {/* Search Result */}
            {searchResult && (
              <div className="mt-4 sm:mt-6 animate-in fade-in slide-in-from-top-4 duration-500">
                {searchResult.found ? (
                  <Alert className="bg-green-50/95 backdrop-blur-sm border-green-200 p-4 shadow-lg">
                    <LinkIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 flex-shrink-0" />
                    <AlertDescription className="ml-2">
                      <div className="space-y-3 sm:space-y-4">
                        <p className="font-semibold text-green-900 text-base sm:text-lg leading-relaxed">
                          Link pembayaran atas nama {searchResult.customerName} ditemukan!
                        </p>
                        <p className="text-gray-700 text-sm sm:text-base break-all leading-relaxed">
                          {searchResult.paymentLink}
                        </p>
                        <Button
                          onClick={() => window.open(searchResult.paymentLink, '_blank')}
                          className="w-full bg-green-600 hover:bg-green-700 text-white font-medium min-h-[48px] h-12 sm:h-11 shadow-md transition-all duration-200 hover:shadow-lg active:scale-95 text-base sm:text-sm"
                        >
                          <LinkIcon className="w-5 h-5 sm:w-4 sm:h-4 mr-2" />
                          Bayar Sekarang
                        </Button>
                      </div>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert className="bg-red-50/95 backdrop-blur-sm border-red-200 p-4 shadow-lg">
                    <X className="h-5 w-5 sm:h-6 sm:w-6 text-red-600 flex-shrink-0" />
                    <AlertDescription className="ml-2">
                      <p className="font-semibold text-red-900 text-base sm:text-base leading-relaxed">
                        Link pembayaran untuk {searchResult.number} tidak ditemukan atau belum terbit
                      </p>
                      <p className="text-gray-600 text-sm mt-2 leading-relaxed">
                        Silakan periksa nomor dan coba lagi
                      </p>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer Info - Kontak WA + Member Dari satu baris */}
        <div className="mt-6 sm:mt-8 flex flex-row items-center justify-center gap-3 sm:gap-6 px-2 flex-wrap">
          {/* Kontak WA */}
          <p className="inline-flex items-center justify-center gap-1 sm:gap-2 flex-wrap leading-relaxed text-sm sm:text-base text-gray-600">
            <span className="text-xs sm:text-sm">Kontak Pelayanan Pelanggan</span>
            <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none">
              <path 
                d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" 
                fill="#25D366"
              />
            </svg>
            <span className="text-xs sm:text-sm">WA:</span>
            <a 
              href="https://wa.me/6285165153959" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 font-semibold hover:underline transition-colors duration-200 text-sm sm:text-base active:scale-95 inline-block"
            >
              6285165153959
            </a>
          </p>

          {/* Member Dari */}
          <div className="flex items-center gap-2 sm:gap-3 flex-nowrap" data-testid="member-dari-section">
            <span className="text-gray-700 font-semibold text-xs sm:text-sm whitespace-nowrap">
              Member Dari :
            </span>
            <img
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_c831c003-18ee-420a-baf1-e328c92b39d3/artifacts/5pbv59qn_LOgo-APj-II.png"
              alt="APJII"
              className="h-8 sm:h-10 w-auto object-contain mix-blend-multiply"
              data-testid="member-logo-apjii"
            />
            <img
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_c831c003-18ee-420a-baf1-e328c92b39d3/artifacts/u2eze3mm_radbooxnet.png"
              alt="Radboox.NET"
              className="h-8 sm:h-10 w-auto object-contain mix-blend-multiply"
              data-testid="member-logo-radboox"
            />
            <img
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_c831c003-18ee-420a-baf1-e328c92b39d3/artifacts/02r4i42c_XENDIT-LOGOArtboard-1%402x-1024x441.png"
              alt="Xendit"
              className="h-8 sm:h-10 w-auto object-contain mix-blend-multiply"
              data-testid="member-logo-xendit"
            />
            <img
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_c831c003-18ee-420a-baf1-e328c92b39d3/artifacts/vhyt8xwx_logo-idwebhost-warna.png"
              alt="IDwebhost"
              className="h-8 sm:h-10 w-auto object-contain mix-blend-multiply"
              data-testid="member-logo-idwebhost"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentLookup;
